import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useInvitationMutations, usePendingInvitations } from '@/api/hooks';
import type { Invitation } from '@/api/schemasMembers';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import type { RowAction } from '@/components/RowActions';
import { Tag } from '@/components/badges';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { showApiFailure } from '@/features/access/accessToasts';
import { formatDate } from '@/lib/format';
import { useNow } from '@/lib/useNow';
import { emailNeedsAttention, invitationSentLabel, rateLimitMessage, roleLabel } from './membersModel';

/** The open invitations: who was invited, as what, what became of the email and when; resend or cancel each. */
export function PendingInvitations() {
  const pending = usePendingInvitations();
  const ops = useOrgInvitationOps();
  return (
    <Card className="enter" data-testid="invitations-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Pending invitations
          {pending.data ? <span data-testid="invitations-count"><Tag>{pending.data.length}</Tag></span> : null}
        </CardTitle>
        <CardDescription>Each invitation expires 7 days after it was sent. Resending sends new sign-in details or a new link; the old ones stop working.</CardDescription>
      </CardHeader>
      <CardContent>
        <InvitationsBody pending={pending} ops={ops} emptyDescription="Everyone you invited has joined. Invite people from the Members card." />
      </CardContent>
    </Card>
  );
}

/** Resend and cancel of an invitation list: the org's own (here) or a client's (the Clients page, SPEC §17.6). */
export interface InvitationOps {
  resend: (invitationId: string) => Promise<Invitation>;
  revoke: (invitationId: string) => Promise<unknown>;
  revoking: boolean;
}

function useOrgInvitationOps(): InvitationOps {
  const { resend, revoke } = useInvitationMutations();
  return { resend: resend.mutateAsync, revoke: revoke.mutateAsync, revoking: revoke.isPending };
}

interface BodyProps {
  pending: UseQueryResult<Invitation[]>;
  ops: InvitationOps;
  emptyDescription: string;
}

/** Loading, error, empty or the table of open invitations. */
export function InvitationsBody({ pending, ops, emptyDescription }: BodyProps) {
  if (pending.isPending) {
    return (
      <div data-testid="invitations-loading" className="flex flex-col gap-2" aria-busy="true" aria-label="Loading invitations">
        {[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (pending.isError) return <ErrorState compact error={pending.error} onRetry={() => void pending.refetch()} title="Couldn't load the invitations" />;
  if (pending.data.length === 0) return <EmptyState testId="invitations-empty" compact icon="mark_email_read" title="No pending invitations" description={emptyDescription} />;
  return <InvitationsTable rows={pending.data} ops={ops} />;
}

function InvitationsTable({ rows, ops }: { rows: Invitation[]; ops: InvitationOps }) {
  const now = useNow(60_000);
  const [canceling, setCanceling] = useState<Invitation | null>(null);
  const onResend = async (i: Invitation) => {
    try {
      const sent = await ops.resend(i.id);
      if (sent.emailStatus === 'failed') toast.warning(`The email to ${i.email} did not go out`, { description: 'Try again in a moment.' });
      else if (sent.emailStatus === 'sign_in_sent') toast.success(`New sign-in email sent to ${i.email}`, { description: 'It carries new sign-in details; their first sign-in with this address accepts the invitation.' });
      else toast.success(`New link sent to ${i.email}`, { description: 'The previous link no longer works.' });
    } catch (e) {
      const limited = rateLimitMessage(e);
      if (limited) toast.warning(`Not resent to ${i.email}`, { description: limited });
      else showApiFailure(e, 'Could not resend the invitation');
    }
  };
  const actions = (i: Invitation): RowAction[] => [
    { key: 'resend', label: 'Resend', icon: 'forward_to_inbox', testId: 'invitation-resend', onSelect: () => void onResend(i) },
    { key: 'cancel', label: 'Cancel', icon: 'cancel', destructive: true, testId: 'invitation-cancel', onSelect: () => setCanceling(i) },
  ];
  const sentCell = (i: Invitation) => <span data-testid="invitation-email-status" className={emailNeedsAttention(i) ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>{invitationSentLabel(i, now)}</span>;
  const columns: CompactColumn<Invitation>[] = [
    { key: 'email', header: 'Email', width: 'w-[45%]', className: 'max-w-0', cell: (i) => <span className="block truncate font-mono text-[13px] text-foreground">{i.email}</span> },
    { key: 'role', header: 'Role', cell: (i) => <Tag>{roleLabel(i.orgRole)}</Tag> },
    { key: 'sent', header: 'Email', hide: '@max-[40rem]:hidden', cell: sentCell },
    { key: 'expires', header: 'Expires', hide: '@max-[48rem]:hidden', cell: (i) => <span className="text-xs text-muted-foreground">{formatDate(i.linkExpiresAt)}</span> },
  ];
  return (
    <>
      <CompactTable
        label="Pending invitations"
        rows={rows}
        columns={columns}
        rowKey={(i) => i.id}
        rowTestId={(i) => `invitation-row-${i.email}`}
        subject={(i) => i.email}
        actions={actions}
        phoneRow={(i) => (
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="truncate font-mono text-[13px] text-foreground">{i.email}</span>
            <span className="flex flex-wrap items-center gap-2"><Tag>{roleLabel(i.orgRole)}</Tag>{sentCell(i)}</span>
          </div>
        )}
      />
      <CancelInvitationDialog invitation={canceling} ops={ops} onDone={() => setCanceling(null)} />
    </>
  );
}

function CancelInvitationDialog({ invitation, ops, onDone }: { invitation: Invitation | null; ops: InvitationOps; onDone: () => void }) {
  const onCancel = async () => {
    if (!invitation) return;
    try {
      await ops.revoke(invitation.id);
      toast.success(`Invitation for ${invitation.email} canceled`, { description: 'Its link no longer works.' });
      onDone();
    } catch (e) {
      showApiFailure(e, 'Could not cancel the invitation');
    }
  };
  return (
    <AlertDialog open={Boolean(invitation)} onOpenChange={(o) => (o ? undefined : onDone())}>
      <AlertDialogContent data-testid="invitation-cancel-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="break-words">Cancel the invitation for {invitation?.email}?</AlertDialogTitle>
          <AlertDialogDescription>Their invitation stops working at once. You can invite them again later.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="invitation-cancel-dismiss">Keep it</AlertDialogCancel>
          <AlertDialogAction destructive data-testid="invitation-cancel-confirm" disabled={ops.revoking} onClick={(e) => { e.preventDefault(); void onCancel(); }}>
            Cancel invitation
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
